import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import {
  getAllUsers,
  getFoundersFirstJournalsForOptimalPaceAnalysis,
  mergeInsatiableJournalEntriesWithDoryRows,
  normalizeDoryJournalRowToInsatiableEntry,
  isDefaultJournalEntry
} from '../../services/funnelService';
import DistributionChart from './DistributionChart';

const Container = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  box-shadow: ${theme.shadows.sm};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.xl};
  flex-wrap: wrap;
`;

const Title = styled.h2`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h2};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin: 0;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: ${theme.spacing.xl};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
`;

const ErrorMessage = styled.div`
  background: ${theme.colors.error};
  color: white;
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  margin-bottom: ${theme.spacing.lg};
  text-align: center;
  line-height: ${theme.typography.lineHeights.normal};
`;

const WarningMessage = styled.div`
  background: ${theme.colors.doryBeige};
  color: ${theme.colors.text};
  border: 1px solid ${theme.colors.doryLighterBlue};
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  margin-bottom: ${theme.spacing.lg};
  line-height: ${theme.typography.lineHeights.normal};
`;

const RefreshButton = styled.button`
  background: transparent;
  color: ${theme.colors.doryDarkerBlue};
  border: none;
  border-radius: ${theme.borderRadius.sm};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  font-weight: ${theme.typography.weights.medium};
  cursor: pointer;
  transition: background ${theme.animation.durations.fast}, color ${theme.animation.durations.fast};
  white-space: nowrap;
  line-height: 1.3;

  &:hover:not(:disabled) {
    background: ${theme.colors.doryBeige};
    color: ${theme.colors.doryBlue};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SummaryStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing.lg};
  margin-top: ${theme.spacing.xl};
`;

const StatCard = styled.div`
  background: ${theme.colors.doryBeige};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.lg};
  text-align: center;
`;

const StatValue = styled.div`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h1};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.primary};
  margin-bottom: ${theme.spacing.sm};
`;

const StatLabel = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: ${theme.spacing.xl};
  color: ${theme.colors.textLight};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
`;

const isProductionUser = (user) => {
  const hostname = user.hostname || '';

  if (!Object.prototype.hasOwnProperty.call(user, 'hostname')) {
    return true;
  }

  return (
    !hostname.includes('localhost') &&
    !hostname.includes('127.0.0.1') &&
    !hostname.includes('test') &&
    hostname.length > 0
  );
};

const getEntryPressureScore = (entry) => {
  if (!entry || typeof entry !== 'object') {
    return null;
  }

  const raw =
    entry.pressureScore !== undefined && entry.pressureScore !== null
      ? entry.pressureScore
      : entry.pressure;

  if (raw === undefined || raw === null) {
    return null;
  }

  const score = Number(raw);
  if (Number.isNaN(score) || score < 0 || score > 10) {
    return null;
  }

  return score;
};

const addPressureScoreValue = (numericScore, pressureValues, distribution) => {
  if (numericScore === undefined || numericScore === null) {
    return;
  }

  const score = Number(numericScore);
  if (Number.isNaN(score)) {
    return;
  }

  const roundedScore = Math.round(score);
  const cappedScore = Math.min(10, Math.max(0, roundedScore));

  pressureValues.push(score);
  distribution[cappedScore]++;
};

/**
 * Collect pressure scores from journal entries that have a valid pressure value.
 * Default (all-slider-at-5) entries are skipped so untouched defaults don't dominate the "5" bucket.
 */
const collectPressureScoresFromEntries = (entries) => {
  const values = [];
  let skippedDefault = 0;
  let skippedMissing = 0;

  (Array.isArray(entries) ? entries : []).forEach((entry) => {
    if (isDefaultJournalEntry(entry)) {
      skippedDefault++;
      return;
    }

    const score = getEntryPressureScore(entry);
    if (score === null) {
      skippedMissing++;
      return;
    }

    values.push(score);
  });

  return { values, skippedDefault, skippedMissing };
};

const normalizeShortcode = (value) => {
  if (value == null) return '';
  const trimmed = String(value).trim().toUpperCase();
  return trimmed;
};

const PressureScoreAnalysisScreen = () => {
  const [pressureData, setPressureData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const analyzePressureScoreData = async () => {
    setLoading(true);
    setError('');

    try {
      console.log('Fetching Insatiable + founders-first journals for pressure score distribution...');
      const [users, foundersFirstResult] = await Promise.all([
        getAllUsers(),
        getFoundersFirstJournalsForOptimalPaceAnalysis().catch((ffError) => {
          console.error('Founders-first journals fetch failed:', ffError);
          return {
            users: [],
            error: ffError.message || 'Failed to load founders-first journals'
          };
        })
      ]);

      const productionUsers = users.filter(isProductionUser);
      const ffUsers = Array.isArray(foundersFirstResult.users) ? foundersFirstResult.users : [];

      const ffByShortCode = new Map();
      ffUsers.forEach((ffUser) => {
        const shortCode = normalizeShortcode(ffUser.shortCode);
        if (!shortCode) return;
        if (!ffByShortCode.has(shortCode)) {
          ffByShortCode.set(shortCode, []);
        }
        ffByShortCode.get(shortCode).push(ffUser);
      });

      console.log(
        `Collecting pressure scores across ${productionUsers.length} Insatiable production users` +
          ` and ${ffUsers.length} founders-first users with journals`
      );

      const pressureValues = [];
      const distribution = {};
      for (let score = 0; score <= 10; score++) {
        distribution[score] = 0;
      }

      let insatiableReadings = 0;
      let foundersFirstOnlyReadings = 0;
      let skippedDefault = 0;
      let skippedMissing = 0;
      let usersWithReadings = 0;
      const handledFfUserIds = new Set();
      const handledShortcodes = new Set();

      productionUsers.forEach((user) => {
        const shortcode = normalizeShortcode(user.shortcode || user.id);
        const ffBundles = shortcode ? ffByShortCode.get(shortcode) || [] : [];
        const doryRows = ffBundles.flatMap((bundle) => {
          handledFfUserIds.add(bundle.userId);
          return bundle.entries || [];
        });
        if (shortcode) {
          handledShortcodes.add(shortcode);
        }

        const mergedEntries = mergeInsatiableJournalEntriesWithDoryRows(
          user.journalEntries || [],
          doryRows
        );
        const collected = collectPressureScoresFromEntries(mergedEntries);
        skippedDefault += collected.skippedDefault;
        skippedMissing += collected.skippedMissing;

        if (collected.values.length === 0) {
          return;
        }

        usersWithReadings++;
        collected.values.forEach((score) => {
          addPressureScoreValue(score, pressureValues, distribution);
          insatiableReadings++;
        });
      });

      ffUsers.forEach((ffUser) => {
        if (handledFfUserIds.has(ffUser.userId)) {
          return;
        }
        const shortCode = normalizeShortcode(ffUser.shortCode);
        if (shortCode && handledShortcodes.has(shortCode)) {
          return;
        }

        const normalizedEntries = (ffUser.entries || [])
          .map((row) => normalizeDoryJournalRowToInsatiableEntry(row))
          .filter(Boolean);
        const collected = collectPressureScoresFromEntries(normalizedEntries);
        skippedDefault += collected.skippedDefault;
        skippedMissing += collected.skippedMissing;

        if (collected.values.length === 0) {
          return;
        }

        usersWithReadings++;
        collected.values.forEach((score) => {
          addPressureScoreValue(score, pressureValues, distribution);
          foundersFirstOnlyReadings++;
        });
      });

      const totalReadings = pressureValues.length;
      const averagePressureScore =
        totalReadings > 0
          ? pressureValues.reduce((sum, val) => sum + val, 0) / totalReadings
          : 0;
      const sortedValues = [...pressureValues].sort((a, b) => a - b);
      const medianPressureScore =
        totalReadings > 0 ? sortedValues[Math.floor(totalReadings / 2)] : 0;

      let mostCommonScore = 0;
      let maxCount = 0;
      for (let score = 0; score <= 10; score++) {
        if (distribution[score] > maxCount) {
          maxCount = distribution[score];
          mostCommonScore = score;
        }
      }

      const analysisData = {
        distribution,
        totalReadings,
        averagePressureScore,
        medianPressureScore,
        mostCommonScore,
        maxCount,
        rawValues: pressureValues,
        insatiableReadings,
        foundersFirstOnlyReadings,
        skippedDefault,
        skippedMissing,
        usersWithReadings,
        foundersFirstError: foundersFirstResult.error || null
      };

      setPressureData(analysisData);
      console.log('Pressure score distribution analysis completed:', analysisData);
    } catch (err) {
      const errorMessage = err.message || 'Failed to load pressure score analysis data. Please try again.';
      setError(`Error: ${errorMessage}`);
      console.error('Error loading pressure score analysis:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    analyzePressureScoreData();
  }, []);

  if (loading) {
    return (
      <Container>
        <LoadingMessage>Collecting pressure scores across all journals...</LoadingMessage>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorMessage>{error}</ErrorMessage>
        <RefreshButton type="button" onClick={analyzePressureScoreData}>
          Retry
        </RefreshButton>
      </Container>
    );
  }

  if (!pressureData || pressureData.totalReadings === 0) {
    return (
      <Container>
        <Header>
          <Title>Pressure Score Distribution</Title>
          <RefreshButton type="button" onClick={analyzePressureScoreData}>
            Refresh
          </RefreshButton>
        </Header>
        {pressureData?.foundersFirstError && (
          <WarningMessage>
            Founders-first data unavailable: {pressureData.foundersFirstError}
          </WarningMessage>
        )}
        <NoDataMessage>
          No pressure score readings available from production journals.
        </NoDataMessage>
      </Container>
    );
  }

  const {
    distribution,
    totalReadings,
    averagePressureScore,
    medianPressureScore,
    mostCommonScore,
    maxCount,
    insatiableReadings,
    foundersFirstOnlyReadings,
    skippedDefault,
    skippedMissing,
    usersWithReadings,
    foundersFirstError
  } = pressureData;

  return (
    <Container>
      <Header>
        <Title>Pressure Score Distribution</Title>
        <RefreshButton type="button" onClick={analyzePressureScoreData}>
          Refresh
        </RefreshButton>
      </Header>

      {foundersFirstError && (
        <WarningMessage>
          Founders-first data unavailable (showing Insatiable only): {foundersFirstError}
        </WarningMessage>
      )}

      <DistributionChart
        distribution={distribution}
        maxCount={maxCount}
      />

      <SummaryStats>
        <StatCard>
          <StatValue>{totalReadings}</StatValue>
          <StatLabel>Pressure Score Readings</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{usersWithReadings}</StatValue>
          <StatLabel>Users Contributing Readings</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{insatiableReadings}</StatValue>
          <StatLabel>From Insatiable Histories</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{foundersFirstOnlyReadings}</StatValue>
          <StatLabel>From founders-first-only Histories</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{skippedDefault}</StatValue>
          <StatLabel>Excluded (Default Journal)</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{skippedMissing}</StatValue>
          <StatLabel>Excluded (No Pressure Score)</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{averagePressureScore.toFixed(1)}</StatValue>
          <StatLabel>Average Pressure Score</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{medianPressureScore.toFixed(1)}</StatValue>
          <StatLabel>Median Pressure Score</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{mostCommonScore}</StatValue>
          <StatLabel>Most Common Pressure Score</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{maxCount}</StatValue>
          <StatLabel>Readings at Most Common Score</StatLabel>
        </StatCard>
      </SummaryStats>
    </Container>
  );
};

export default PressureScoreAnalysisScreen;
